import type { ReputationImageStyle } from '@reach/shared-types/automation-template';
import { ReputationTemplateTypeEnum } from '@reach/shared-types/business-automation';

export const IMAGE_STYLE_OPTIONS: { value: ReputationImageStyle; label: string }[] = [
  { value: 'illustration', label: 'Illustration' },
  { value: 'photorealistic', label: 'Photo-like' },
  { value: 'graphic_design', label: 'Bold & Colorful' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'watercolor', label: 'Watercolor' },
];

export const VERTICAL_FIELD_LABEL = 'What kind of businesses are your customers?';
export const VERTICAL_FIELD_PLACEHOLDER = 'e.g. pet care and boarding facilities';
export const VERTICAL_FIELD_HELP =
  'Describe the businesses on your platform, not your own company. These templates are sent by each of your tenants to their own customers, so the wording has to read naturally for any of them.';
export const ONE_SHOT_WARNING =
  'Copy is written once, as these automations are added — there is no way to rewrite them later. You can still edit any message by hand, and nothing sends until you choose which tenants get it.';
export const SKIP_EXPLANATION =
  'Or add them with the generic wording they ship with, and write your own.';
export const SKIP_BUTTON_LABEL = 'Add with default copy';
export const GENERATE_BUTTON_LABEL = 'Add and write copy';
export const GENERATE_BUTTON_PENDING_LABEL = 'Adding…';
export const IMAGE_TOGGLE_LABEL = 'Also generate imagery';
export const IMAGE_TOGGLE_HELP =
  'Replaces the stock illustrations with artwork for your vertical. Takes noticeably longer than copy alone.';
export const IMAGE_STYLE_FIELD_LABEL = 'Image style';
export const MAX_VERTICAL_DESCRIPTION_LENGTH = 500;

export const DIALOG_TITLE = 'Add default review automations';
export const DIALOG_DESCRIPTION =
  'Choose default review automations and optionally customize their copy for your vertical.';
export const DIALOG_CLOSE_LABEL = 'Close dialog';
export const DESCRIBE_STEP_HEADING = 'Write the copy for your vertical';
export const TEMPLATE_SELECTION_HEADING = 'Choose the automations to add';
export const TEMPLATE_LOADING_LABEL = 'Loading…';
export const NO_DEFAULT_TEMPLATES_LABEL = 'No default automations available.';
export const SELECT_ALL_LABEL = 'Select all';
export const ADDED_LABEL = 'Added';
export const NEXT_BUTTON_LABEL = 'Next';
export const BACK_BUTTON_LABEL = 'Back';
export const DONE_BUTTON_LABEL = 'Done';
export const CLOSE_BUTTON_LABEL = 'Close';
export const PROGRESS_SUBTITLE_DONE =
  'Your automations are ready as drafts. Choose which tenants get them from each automation.';
export const PROGRESS_SUBTITLE_RUNNING =
  'Writing your copy. You can close this — it keeps running, and the Reputation tab will tell you when it is done.';
export const GENERATION_FAILED_FALLBACK = 'Generation failed. Your automations kept their default copy.';
export const GENERATION_RUNNING_MESSAGE =
  'Writing copy for your review automations. You can keep working — we will tell you when it is done.';
export const GENERATION_BANNER_FAILED_FALLBACK =
  'We could not write your copy. Your automations kept their default wording.';
export const GENERATION_STATUS_UNAVAILABLE_MESSAGE =
  'We lost track of your copy generation. Check your automations to see what was written; anything not rewritten kept its default wording.';
export const DISMISS_LABEL = 'Dismiss';

/** Survives a dialog close or a reload so a run cannot finish unseen. */
export const ACTIVE_GENERATION_STORAGE_KEY = 'reach-reputation-generation-job';

export const MESSAGE_LABEL_BY_TEMPLATE_TYPE = {
  [ReputationTemplateTypeEnum.FEEDBACK_REQUEST]: 'Feedback request',
  [ReputationTemplateTypeEnum.FEEDBACK_REQUEST_REMINDER]: 'Feedback reminder',
  [ReputationTemplateTypeEnum.GOOGLE_REVIEW]: 'Google review request',
  [ReputationTemplateTypeEnum.GOOGLE_REVIEW_REMINDER]: 'Google review reminder',
  [ReputationTemplateTypeEnum.COMBINED_REQUEST]: 'Feedback and review request',
  [ReputationTemplateTypeEnum.COMBINED_REQUEST_REMINDER]: 'Feedback and review reminder',
} satisfies Record<ReputationTemplateTypeEnum, string>;

export const KEPT_ORIGINAL_COPY_NOTE = 'Kept its original wording';
export const KEPT_ORIGINAL_IMAGE_NOTE = 'Kept its original illustration';
